import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import AuditTrailPanel from "@/components/AuditTrailPanel";
import ESignatureDialog from "@/components/ESignatureDialog";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sparkles, ArrowRight, Loader2, ShieldCheck, PlusCircle } from "lucide-react";
import { toast } from "sonner";

const STATES = ["Open", "Investigation", "RootCause", "ActionPlan", "Implementation", "Effectiveness", "Closed"];

export default function CAPADetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [rcaLoading, setRcaLoading] = useState(false);
  const [rcaProblem, setRcaProblem] = useState("");
  const [action, setAction] = useState({ action_type: "Corrective", description: "", assignee_email: "", due_date: "" });

  // Unified e-sig state
  const [esignOpen, setEsignOpen] = useState(false);
  const [esignConfig, setEsignConfig] = useState(null);

  // Effectiveness dialog
  const [effDialogOpen, setEffDialogOpen] = useState(false);
  const [effForm, setEffForm] = useState({ result: "Effective", evidence: "" });

  const load = async () => {
    const { data } = await api.get(`/capa/${id}`);
    setC(data);
    setRefreshKey((k) => k + 1);
  };
  useEffect(() => { load(); }, [id]);

  const runRCA = async () => {
    if (!rcaProblem) return toast.error("Enter problem statement");
    setRcaLoading(true);
    try {
      await api.post(`/capa/${id}/rca`, { problem_statement: rcaProblem });
      toast.success("RCA generated");
      load();
    } catch { toast.error("RCA failed"); }
    finally { setRcaLoading(false); }
  };

  const addAction = async () => {
    if (!action.description) return toast.error("Description required");
    await api.post(`/capa/${id}/actions`, action);
    toast.success("Action added");
    setAction({ action_type: "Corrective", description: "", assignee_email: "", due_date: "" });
    load();
  };

  const requestTransition = (toState) => {
    setEsignConfig({
      kind: "transition",
      toState,
      action: `capa_transition_${toState}`,
      meaning: toState === "Closed" ? "Approval" : "Review",
      title: `Sign to advance CAPA to ${toState}`,
    });
    setEsignOpen(true);
  };

  const requestEffectiveness = () => {
    if (!effForm.evidence) return toast.error("Provide evidence");
    setEsignConfig({
      kind: "effectiveness",
      action: "capa_effectiveness",
      meaning: "Effectiveness Check",
      title: "Sign effectiveness check",
    });
    setEffDialogOpen(false);
    setEsignOpen(true);
  };

  const onSigned = async (sig) => {
    try {
      if (esignConfig.kind === "transition") {
        await api.post(`/capa/${id}/transition`, {
          to_state: esignConfig.toState,
          signature_id: sig.signature_id,
          notes: sig.reason,
        });
        toast.success(`Moved to ${esignConfig.toState}`);
      } else if (esignConfig.kind === "effectiveness") {
        await api.post(`/capa/${id}/effectiveness`, {
          ...effForm,
          signature_id: sig.signature_id,
        });
        toast.success("Effectiveness recorded");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setEsignConfig(null);
    }
  };

  if (!c) return <DashboardLayout><div className="p-10">Loading…</div></DashboardLayout>;
  const stateIdx = STATES.indexOf(c.state);
  const nextState = stateIdx >= 0 && stateIdx < STATES.length - 1 ? STATES[stateIdx + 1] : null;

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-6xl">
        <div className="vq-overline mb-2 font-mono">{c.capa_id}</div>
        <h1 className="font-display font-extrabold text-3xl tracking-tight text-slate-900 mb-2">{c.title}</h1>
        <p className="text-slate-600 mb-6 max-w-3xl">{c.description}</p>

        <div className="flex items-center flex-wrap gap-2 mb-8">
          {STATES.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <span data-testid={`capa-state-${s}`} className={`px-3 py-1 rounded text-xs font-mono ${
                s === c.state ? "bg-slate-900 text-white" : i < stateIdx ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"
              }`}>{s}</span>
              {i < STATES.length - 1 && <ArrowRight className="w-3 h-3 text-slate-300" />}
            </div>
          ))}
        </div>

        <div className="flex gap-3 mb-10 flex-wrap">
          {nextState && c.state !== "Effectiveness" && (
            <Button onClick={() => requestTransition(nextState)} data-testid="capa-transition-btn"
                    className="bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
              <ShieldCheck className="w-4 h-4 mr-2" /> Advance to {nextState} — with e-sig
            </Button>
          )}
          {c.state === "Effectiveness" && !c.effectiveness && (
            <Button onClick={() => setEffDialogOpen(true)} data-testid="capa-effectiveness-btn"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
              Record Effectiveness Check
            </Button>
          )}
          {c.state === "Effectiveness" && c.effectiveness && (
            <Button onClick={() => requestTransition("Closed")} data-testid="capa-close-btn"
                    className="bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
              Close CAPA — with e-sig
            </Button>
          )}
        </div>

        <section className="vq-card p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-[#0055FF]" />
            <h2 className="font-display font-bold text-lg text-slate-900">Root Cause Analysis (5-Why, AI-assisted)</h2>
          </div>
          {c.root_cause ? (
            <div className="space-y-3">
              <div className="p-3 bg-slate-50 border border-slate-200 rounded">
                <div className="vq-overline mb-1">Identified root cause</div>
                <div className="text-sm font-semibold text-slate-900" data-testid="capa-rca-root">{c.root_cause.root_cause}</div>
              </div>
              <div className="space-y-1">
                {(c.root_cause.whys || []).map((w) => (
                  <div key={w.why} className="text-sm border-l-2 border-[#0055FF] pl-3 py-1">
                    <div className="font-mono text-[10px] text-[#0055FF]">WHY #{w.why}</div>
                    <div className="text-slate-900 font-medium">{w.question}</div>
                    <div className="text-slate-600 text-xs">{w.answer}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <Textarea data-testid="capa-rca-input" placeholder="Restate the problem in one clear sentence…"
                        value={rcaProblem} onChange={(e) => setRcaProblem(e.target.value)} rows={2} />
              <Button onClick={runRCA} disabled={rcaLoading} data-testid="capa-rca-run"
                      className="bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
                {rcaLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing…</> : "Run 5-Why analysis"}
              </Button>
            </div>
          )}
        </section>

        <section className="vq-card p-6 mb-6">
          <h2 className="font-display font-bold text-lg text-slate-900 mb-4">Actions ({(c.actions || []).length})</h2>
          <div className="space-y-2 mb-4">
            {(c.actions || []).map((a) => (
              <div key={a.action_id} className="border border-slate-200 rounded p-3 flex items-center gap-3">
                <span className={`px-2 py-0.5 rounded text-[10px] font-mono ${a.action_type === "Corrective" ? "bg-blue-50 text-blue-700" : "bg-violet-50 text-violet-700"}`}>{a.action_type}</span>
                <div className="flex-1 text-sm text-slate-900">{a.description}</div>
                <span className="text-xs font-mono text-slate-500">{a.assignee_email || "—"}</span>
                <span className="text-xs font-mono text-slate-500">{a.due_date || "—"}</span>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
            <Select value={action.action_type} onValueChange={(v) => setAction({ ...action, action_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Corrective">Corrective</SelectItem>
                <SelectItem value="Preventive">Preventive</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="Action description" value={action.description}
                   onChange={(e) => setAction({ ...action, description: e.target.value })}
                   className="md:col-span-2" data-testid="capa-action-desc" />
            <Input placeholder="Assignee email" value={action.assignee_email}
                   onChange={(e) => setAction({ ...action, assignee_email: e.target.value })} />
            <Button onClick={addAction} data-testid="capa-action-add"
                    className="bg-slate-900 hover:bg-[#0055FF] text-white">
              <PlusCircle className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
        </section>

        {c.effectiveness && (
          <section className="vq-card p-6 mb-6 bg-emerald-50 border-emerald-200">
            <h2 className="font-display font-bold text-lg text-slate-900 mb-2">Effectiveness Check</h2>
            <div className="text-sm text-slate-900"><strong>{c.effectiveness.result}</strong> — {c.effectiveness.evidence}</div>
            <div className="text-xs font-mono text-slate-500 mt-2">by {c.effectiveness.checked_by} • {new Date(c.effectiveness.checked_at).toLocaleString()}</div>
          </section>
        )}

        <section className="mb-10">
          <h2 className="font-display font-bold text-lg text-slate-900 mb-4">Compliance Records</h2>
          <AuditTrailPanel entityType="capa" entityId={id} refreshKey={refreshKey} />
        </section>
      </div>

      {/* Effectiveness form dialog */}
      <Dialog open={effDialogOpen} onOpenChange={setEffDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">Effectiveness Check</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs font-semibold">Result</Label>
            <Select value={effForm.result} onValueChange={(v) => setEffForm({ ...effForm, result: v })}>
              <SelectTrigger data-testid="capa-eff-result"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Effective", "Partial", "Not Effective"].map((x) => (<SelectItem key={x} value={x}>{x}</SelectItem>))}
              </SelectContent>
            </Select>
            <Label className="text-xs font-semibold">Evidence</Label>
            <Textarea data-testid="capa-eff-evidence" value={effForm.evidence}
                      onChange={(e) => setEffForm({ ...effForm, evidence: e.target.value })} rows={4} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEffDialogOpen(false)}>Cancel</Button>
            <Button onClick={requestEffectiveness} data-testid="capa-eff-submit"
                    className="bg-slate-900 hover:bg-[#0055FF] text-white">
              Continue to e-signature
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unified e-sig dialog */}
      {esignConfig && (
        <ESignatureDialog
          open={esignOpen}
          onOpenChange={setEsignOpen}
          entityType="capa"
          entityId={id}
          action={esignConfig.action}
          defaultMeaning={esignConfig.meaning}
          title={esignConfig.title}
          onSigned={onSigned}
        />
      )}
    </DashboardLayout>
  );
}
